import { useMemo } from 'react';
import { Combobox, Field, Input, type ComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import type { MarketingUsableEmailAccount } from '@/lib/types';

type UsableAccount = MarketingUsableEmailAccount;

export interface SenderSectionProps {
  accounts: UsableAccount[] | null;
  accountsLoading: boolean;
  accountsError: string | null;
  /** The account the saved campaign names — kept selectable even if it is not usable by this reader. */
  storedAccount: { id: number; name: string; fromEmail: string } | null;
  emailAccountId: number | null;
  fromNameOverride: string;
  replyToOverride: string;
  onChange(patch: Partial<{ emailAccountId: number | null; fromNameOverride: string; replyToOverride: string }>): void;
  disabled?: boolean;
  errors?: Partial<Record<'emailAccountId' | 'fromNameOverride' | 'replyToOverride', string>>;
}

export function SenderSection({
  accounts,
  accountsLoading,
  accountsError,
  storedAccount,
  emailAccountId,
  fromNameOverride,
  replyToOverride,
  onChange,
  disabled,
  errors = {},
}: SenderSectionProps) {
  const options = useMemo<ComboboxOption[]>(() => {
    const list: ComboboxOption[] = (accounts ?? []).map((account) => {
      const left = Math.max(account.dailyLimit - account.sentToday, 0);
      return {
        value: String(account.id),
        label: `${account.name} · ${account.fromEmail}`,
        hint: `${left.toLocaleString()} of ${account.dailyLimit.toLocaleString()} left today`,
      };
    });
    if (storedAccount && !list.some((o) => o.value === String(storedAccount.id))) {
      list.unshift({
        value: String(storedAccount.id),
        label: `${storedAccount.name} · ${storedAccount.fromEmail}`,
        hint: 'Not available to your account',
      });
    }
    return list;
  }, [accounts, storedAccount]);

  const selected = accounts?.find((a) => a.id === emailAccountId) ?? null;

  return (
    <div className="grid min-w-0 gap-4">
      {accountsError && (
        <Notice tone="danger" role="alert">
          Couldn&rsquo;t load email accounts. {accountsError}
        </Notice>
      )}
      <Field label="Send from" required error={errors.emailAccountId}>
        {({ id, 'aria-describedby': describedBy }) => (
          <Combobox
            id={id}
            aria-describedby={describedBy}
            value={emailAccountId === null ? '' : String(emailAccountId)}
            onChange={(value) => onChange({ emailAccountId: value === '' ? null : Number(value) })}
            options={options}
            placeholder="Choose an email account…"
            searchPlaceholder="Search accounts…"
            loading={accountsLoading}
            disabled={disabled}
            invalid={Boolean(errors.emailAccountId)}
          />
        )}
      </Field>
      {!accountsLoading && !accountsError && accounts !== null && accounts.length === 0 && (
        <p className="m-0 text-xs text-fg-muted">No email account is available to your account.</p>
      )}
      <div className="grid min-w-0 gap-4 md:grid-cols-2">
        <Field label="From name" error={errors.fromNameOverride}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={fromNameOverride}
              maxLength={120}
              placeholder={selected?.fromName ?? ''}
              disabled={disabled}
              invalid={Boolean(errors.fromNameOverride)}
              onChange={(e) => onChange({ fromNameOverride: e.target.value })}
            />
          )}
        </Field>
        <Field label="Reply-to" error={errors.replyToOverride}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              type="email"
              aria-describedby={describedBy}
              value={replyToOverride}
              maxLength={320}
              placeholder={selected?.fromEmail ?? ''}
              disabled={disabled}
              invalid={Boolean(errors.replyToOverride)}
              onChange={(e) => onChange({ replyToOverride: e.target.value })}
            />
          )}
        </Field>
      </div>
    </div>
  );
}
